/**
 * Done. / ChiroCPD — ExportField Feature Component
 *
 * Three visual states per design spec:
 *   complete — solid 3px left border (success-500), content + CopyButton + AILabel if AI
 *   partial  — dashed 3px left border (warning-500), content + CopyButton + guidance link
 *   empty    — no border, surface-muted background, EmptyState (CopyButton hidden)
 *
 * role="region" + aria-labelledby for screen reader navigation.
 * Content div has tabindex="0" so keyboard users can read long content.
 */

import { AILabel } from '@/components/ui/AILabel';
import { CopyButton } from '@/components/ui/CopyButton';
import { EmptyState } from '@/components/ui/EmptyState';
import { cx } from '@/lib/utils';
import { getFieldGuidance } from '@/lib/portal-field-mappings';
import type { ExportFieldData } from '@/lib/types';

interface ExportFieldProps {
  field: ExportFieldData;
}

export function ExportField({ field }: ExportFieldProps): React.ReactElement {
  const headingId = `export-field-${field.fieldName}-heading`;
  const guidance = getFieldGuidance(field.fieldName);

  return (
    <article
      role="region"
      aria-labelledby={headingId}
      className={cx(
        'rounded-[var(--radius-md)] transition-all',
        field.status === 'complete' && 'export-field--complete',
        field.status === 'partial' && 'export-field--partial',
        field.status === 'empty' && 'rounded-none',
      )}
    >
      {/* Field header */}
      <div className="flex items-start justify-between gap-4 mb-2">
        <div>
          <h3
            id={headingId}
            className="text-xs font-semibold uppercase tracking-wider text-[var(--text-secondary)]"
          >
            {field.fieldLabel}
          </h3>
          <p className="text-xs text-[var(--text-muted)] mt-0.5">
            {field.fieldType === 'number' ? 'Numeric value' : 'Free text'}
            {' · '}Field {field.fieldOrder} of 8
          </p>
        </div>

        {/* CopyButton — hidden when empty */}
        {field.status !== 'empty' && (
          <CopyButton
            text={field.value}
            fieldName={field.fieldLabel}
          />
        )}
      </div>

      {/* Field content */}
      {field.status === 'empty' ? (
        <EmptyState
          fieldName={field.fieldLabel}
          guidance={guidance}
          actionHref={
            field.fieldName === 'outcome' || field.fieldName === 'review'
              ? '/activities?filter=unreflected'
              : field.fieldName === 'rationale'
                ? '/activities'
                : '/activities'
          }
          actionLabel={
            field.fieldName === 'outcome' || field.fieldName === 'review'
              ? '→ Add reflections'
              : '→ Log an activity'
          }
        />
      ) : (
        <div>
          {/* Partial state guidance */}
          {field.status === 'partial' && field.guidanceText && (
            <div className="mb-2 rounded bg-[var(--color-warning-100)] px-3 py-2 text-xs text-amber-800">
              {field.guidanceText}
              {field.actionHref && (
                <a
                  href={field.actionHref}
                  className="ml-1 font-medium underline hover:no-underline"
                >
                  → Add reflections
                </a>
              )}
            </div>
          )}

          {/* Content */}
          {/* role="textbox" aria-readonly makes this a readable/selectable text region */}
          <div
            role="textbox"
            aria-readonly="true"
            aria-multiline="true"
            tabIndex={0}
            className={cx(
              'rounded-[var(--radius-sm)] bg-[var(--surface-subtle)] px-3 py-3 text-sm text-[var(--text-primary)]',
              'whitespace-pre-wrap leading-relaxed',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-done-500)]',
            )}
            aria-label={`${field.fieldLabel} content`}
          >
            {field.value}
          </div>

          {/* AI label — always visible when AI generated */}
          {field.aiGenerated && (
            <div className="mt-2 flex justify-end">
              <AILabel />
            </div>
          )}
        </div>
      )}
    </article>
  );
}
